import React, { useRef, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  ListBulletIcon,
  NumberedListIcon,
  LinkIcon
} from '@heroicons/react/24/outline';

const RichTextEditor = ({ value, onChange, placeholder }) => {
  const editorRef = useRef(null);
  const [history, setHistory] = useState([value || '']);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [charCount, setCharCount] = useState(0);

  // Initialize editor content
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || '';
      updateCharCount();
    }
  }, []);

  const updateCharCount = () => {
    if (editorRef.current) {
      const text = editorRef.current.innerText || '';
      setCharCount(text.length);
    }
  };

  const handleInput = () => {
    if (!editorRef.current) return;
    
    const newValue = editorRef.current.innerHTML;
    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push(newValue);
    
    // Limit history to 50 items
    if (newHistory.length > 50) {
      newHistory.shift();
    } else {
      setHistoryIndex(newHistory.length - 1);
    }
    
    setHistory(newHistory);
    onChange({ target: { value: newValue } });
    updateCharCount();
  };

  const undo = () => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      if (editorRef.current) {
        editorRef.current.innerHTML = history[newIndex];
      }
      onChange({ target: { value: history[newIndex] } });
      updateCharCount();
    }
  };

  const redo = () => {
    if (historyIndex < history.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      if (editorRef.current) {
        editorRef.current.innerHTML = history[newIndex];
      }
      onChange({ target: { value: history[newIndex] } });
      updateCharCount();
    }
  };

  const execCommand = (command, value = null) => {
    // Ensure editor has focus before executing command
    if (editorRef.current) {
      editorRef.current.focus();
    }
    document.execCommand(command, false, value);
    setTimeout(handleInput, 10);
  };

  const makeBold = () => execCommand('bold');
  const makeItalic = () => execCommand('italic');
  const makeUnderline = () => execCommand('underline');
  const makeHeading = () => execCommand('formatBlock', '<h2>');
  
  const changeTextColor = (color) => {
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('foreColor', false, color);
      setTimeout(handleInput, 10);
    }
  };
  
  const makeBulletList = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertUnorderedList', false, null);
      setTimeout(handleInput, 10);
    }
  };
  
  const makeNumberedList = (e) => {
    e.preventDefault();
    if (editorRef.current) {
      editorRef.current.focus();
      document.execCommand('insertOrderedList', false, null);
      setTimeout(handleInput, 10);
    }
  };
  
  const insertLink = () => {
    const url = prompt('Enter the URL:');
    if (url) {
      execCommand('createLink', url);
    }
  };

  const handleKeyDown = (e) => {
    // Ctrl+Z for undo
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      e.preventDefault();
      undo();
    }
    // Ctrl+Shift+Z or Ctrl+Y for redo
    if (((e.ctrlKey || e.metaKey) && e.shiftKey && e.key === 'z') || 
        ((e.ctrlKey || e.metaKey) && e.key === 'y')) {
      e.preventDefault();
      redo();
    }
    // Ctrl+B for bold
    if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
      e.preventDefault();
      makeBold();
    }
    // Ctrl+I for italic
    if ((e.ctrlKey || e.metaKey) && e.key === 'i') {
      e.preventDefault();
      makeItalic();
    }
    // Ctrl+U for underline
    if ((e.ctrlKey || e.metaKey) && e.key === 'u') {
      e.preventDefault();
      makeUnderline();
    }
  };

  return (
    <div className="rich-text-editor w-full">
      {/* Toolbar */}
      <div className="border border-gray-300 rounded-t-lg bg-gray-50 p-2 flex items-center gap-1 flex-wrap">
        {/* Undo/Redo */}
        <div className="flex gap-1 pr-2 border-r border-gray-300">
          <button
            type="button"
            onClick={undo}
            disabled={historyIndex === 0}
            className="p-2 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Undo (Ctrl+Z)"
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={historyIndex >= history.length - 1}
            className="p-2 hover:bg-gray-200 rounded disabled:opacity-30 disabled:cursor-not-allowed"
            title="Redo (Ctrl+Y)"
          >
            <ArrowUturnRightIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Text Formatting */}
        <div className="flex gap-1 pr-2 border-r border-gray-300">
          <button
            type="button"
            onClick={makeBold}
            className="px-3 py-1 hover:bg-gray-200 rounded font-bold"
            title="Bold (Ctrl+B)"
          >
            B
          </button>
          <button
            type="button"
            onClick={makeItalic}
            className="px-3 py-1 hover:bg-gray-200 rounded italic"
            title="Italic (Ctrl+I)"
          >
            I
          </button>
          <button
            type="button"
            onClick={makeUnderline}
            className="px-3 py-1 hover:bg-gray-200 rounded underline"
            title="Underline (Ctrl+U)"
          >
            U
          </button>
        </div>

        {/* Text Color */}
        <div className="flex gap-1 pr-2 border-r border-gray-300 items-center">
          <span className="text-xs text-gray-600 px-1">Color:</span>
          <button
            type="button"
            onClick={() => changeTextColor('#000000')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#000000' }}
            title="Black"
          />
          <button
            type="button"
            onClick={() => changeTextColor('#dc2626')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#dc2626' }}
            title="Red"
          />
          <button
            type="button"
            onClick={() => changeTextColor('#2563eb')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#2563eb' }}
            title="Blue"
          />
          <button
            type="button"
            onClick={() => changeTextColor('#16a34a')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#16a34a' }}
            title="Green"
          />
          <button
            type="button"
            onClick={() => changeTextColor('#ea580c')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#ea580c' }}
            title="Orange"
          />
          <button
            type="button"
            onClick={() => changeTextColor('#9333ea')}
            className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
            style={{ backgroundColor: '#9333ea' }}
            title="Purple"
          />
        </div>

        {/* Heading */}
        <div className="flex gap-1 pr-2 border-r border-gray-300">
          <button
            type="button"
            onClick={makeHeading}
            className="px-3 py-1 hover:bg-gray-200 rounded font-semibold"
            title="Heading"
          >
            H2
          </button>
        </div>

        {/* Lists */}
        <div className="flex gap-1 pr-2 border-r border-gray-300">
          <button
            type="button"
            onClick={makeBulletList}
            className="p-2 hover:bg-gray-200 rounded"
            title="Bullet List"
          >
            <ListBulletIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={makeNumberedList}
            className="p-2 hover:bg-gray-200 rounded"
            title="Numbered List"
          >
            <NumberedListIcon className="h-4 w-4" />
          </button>
        </div>

        {/* Link */}
        <div className="flex gap-1">
          <button
            type="button"
            onClick={insertLink}
            className="p-2 hover:bg-gray-200 rounded"
            title="Insert Link"
          >
            <LinkIcon className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable
        role="textbox"
        aria-multiline="true"
        aria-label="Rich text editor"
        tabIndex={0}
        onInput={handleInput}
        onKeyDown={handleKeyDown}
        className="border border-t-0 border-gray-300 rounded-b-lg p-4 min-h-[200px] max-h-[500px] overflow-y-auto focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white"
        data-placeholder={placeholder}
        style={{
          lineHeight: '1.6'
        }}
      />

      {/* Footer */}
      <div className="mt-2 text-sm text-gray-500 flex justify-between">
        <div className="space-x-3">
          <span>💡 Shortcuts: Ctrl+B (Bold) | Ctrl+I (Italic) | Ctrl+U (Underline) | Ctrl+Z (Undo)</span>
        </div>
        <div>
          <span className="font-mono">{charCount} characters</span>
        </div>
      </div>

      <style>{`
        .rich-text-editor [contenteditable][data-placeholder]:empty:before {
          content: attr(data-placeholder);
          color: #9ca3af;
          pointer-events: none;
          position: absolute;
        }
        .rich-text-editor [contenteditable] h2 {
          font-size: 1.5em;
          font-weight: 600;
          margin: 0.5em 0;
        }
        .rich-text-editor [contenteditable] ul,
        .rich-text-editor [contenteditable] ol {
          margin: 0.5em 0;
          padding-left: 2em;
        }
        .rich-text-editor [contenteditable] a {
          color: #2563eb;
          text-decoration: underline;
        }
      `}</style>
    </div>
  );
};

RichTextEditor.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string
};

RichTextEditor.defaultProps = {
  placeholder: 'Start typing...'
};

export default RichTextEditor;
